import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Share2, ExternalLink, Loader2, ImageOff, Compass } from "lucide-react";
import { devLog } from "@/lib/log";

/**
 * PhotoViewer — full-screen receipt/photo viewer that opens in a new tab
 * from any clickable photo in the app. Solves the mobile dead-end problem:
 *
 *   • Bookmark-safe URL: /photo?src=<encoded-api-path>&name=<file>&token=<t>
 *     Public route (no auth wrapper) — iOS Home-Screen PWAs open
 *     `target="_blank"` links in EXTERNAL Safari, which doesn't share the
 *     PWA's cookie jar. The `token=` in the URL is a short-lived signed
 *     JWT (`file` purpose, 5-min TTL) that authenticates the image fetch
 *     without cookies. See `useFileToken` hook + `create_download_token`
 *     backend helper.
 *
 *   • Share button uses the Web Share API — first fetches the image as a
 *     File so iOS Safari can attach it synchronously to the tap gesture
 *     (otherwise the share sheet is blocked).
 *
 *   • Close button tries `window.close()` first (works when opened via
 *     target='_blank'), then falls back to history/back or /history.
 *
 *   • "Open in Safari" escape hatch links directly at the raw signed image
 *     URL so tradies who prefer Safari's native long-press-to-save gesture
 *     never feel trapped.
 */
export default function PhotoViewer() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const src = params.get("src") || "";
  const token = params.get("token") || "";
  const name = params.get("name") || "receipt";
  const supplier = params.get("supplier") || "";

  const imageUrl = useMemo(() => {
    if (!src) return "";
    // Preserve absolute URLs; otherwise assume it's an /api/files/... path.
    if (/^https?:\/\//i.test(src)) return src;
    const clean = src.startsWith("/") ? src : `/api/files/${src}`;
    const suffix = token ? `${clean.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}` : "";
    return `${API_BASE}${clean}${suffix}`;
  }, [src, token]);

  const filename = useMemo(() => {
    const slug = (supplier || name || "receipt")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "receipt";
    const extMatch = imageUrl.match(/\.(jpe?g|png|webp|heic)(?:$|\?)/i);
    const ext = extMatch ? extMatch[1].toLowerCase().replace("jpeg", "jpg") : "jpg";
    return `${slug}.${ext}`;
  }, [supplier, name, imageUrl]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [canShare, setCanShare] = useState(false);

  // Detect iOS Home-Screen PWA context so we can show the tradie a hint
  // about the "Open in Safari" button (this is what fixes the "trapped
  // in the PWA" complaint — target='_blank' from a PWA opens Safari but
  // the /photo route still needs the token in the URL for image auth).
  const [isStandalonePWA, setIsStandalonePWA] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const standalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      // iOS specific — legacy but still reported by iOS 16+.
      window.navigator?.standalone === true;
    setIsStandalonePWA(Boolean(standalone));
  }, []);

  // Prefetch the image as a File so Web Share can attach it synchronously
  // when the user taps Share (iOS Safari otherwise blocks async share).
  const [fileCache, setFileCache] = useState(null);
  useEffect(() => {
    if (!imageUrl) return;
    let cancelled = false;
    (async () => {
      try {
        // No credentials on this fetch — the token in the URL is what
        // authenticates. This means the pre-fetch works even in external
        // Safari (where the PWA cookie doesn't reach).
        const res = await fetch(imageUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const f = new File([blob], filename, { type: blob.type || "image/jpeg" });
        if (cancelled) return;
        setFileCache(f);
        const supports =
          typeof navigator !== "undefined" &&
          typeof navigator.share === "function" &&
          typeof navigator.canShare === "function" &&
          navigator.canShare({ files: [f] });
        setCanShare(Boolean(supports));
      } catch (err) {
        // Non-fatal — Share button will still show but fall back to text/url.
        devLog.debug("photo prefetch for share failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, [imageUrl, filename]);

  const onShare = useCallback(async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const shareTitle = supplier
        ? `Receipt — ${supplier}`
        : `Receipt${name && name !== "receipt" ? ` — ${name}` : ""}`;
      const shareText = `${shareTitle} (via BuildIntel AI)`;

      if (
        fileCache &&
        typeof navigator !== "undefined" &&
        typeof navigator.share === "function" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [fileCache] })
      ) {
        await navigator.share({
          files: [fileCache],
          title: shareTitle,
          text: shareText,
        });
        return;
      }

      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share({ title: shareTitle, text: shareText, url: imageUrl });
        return;
      }

      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(imageUrl);
        toast.success("Image link copied — paste into WhatsApp or Email");
        return;
      }

      window.open(imageUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      if (err && err.name === "AbortError") return;
      toast.error("Share failed — try Open in Safari instead");
    } finally {
      setSharing(false);
    }
  }, [sharing, fileCache, imageUrl, supplier, name]);

  const onClose = useCallback(() => {
    try {
      window.close();
    } catch (err) {
      devLog.debug("window.close blocked (expected when not opened by _blank)", err);
    }
    setTimeout(() => {
      if (!document.hidden) {
        if (window.history.length > 1) {
          nav(-1);
        } else {
          nav("/history", { replace: true });
        }
      }
    }, 120);
  }, [nav]);

  if (!src) {
    return (
      <div className="min-h-screen bg-[#0F0F10] text-white flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <ImageOff className="w-8 h-8 text-white/40 mx-auto" />
          <div className="font-heading text-lg font-bold">No photo to display</div>
          <button
            data-testid="photo-viewer-back"
            onClick={onClose}
            className="h-10 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-heading font-bold uppercase tracking-wider inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Back to app
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0F10] text-white flex flex-col" data-testid="photo-viewer">
      {/* Top bar — Close on the left, quick meta in the middle. */}
      <header className="sticky top-0 z-20 bg-[#1F1F1F]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="photo-viewer-close"
            onClick={onClose}
            className="h-10 px-3 rounded-lg bg-white/5 hover:bg-white/10 active:scale-95 transition inline-flex items-center gap-1.5 text-sm font-heading font-bold uppercase tracking-wider"
            aria-label="Close"
          >
            <ArrowLeft className="w-4 h-4" /> Close
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-extrabold text-white truncate text-sm">
              {supplier || "Receipt photo"}
            </div>
            <div className="text-[10px] text-white/40 truncate">
              BuildIntel AI · {filename}
            </div>
          </div>
        </div>
      </header>

      {/* Image stage */}
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-4">
        {isStandalonePWA && (
          <div
            data-testid="photo-viewer-pwa-hint"
            className="mb-3 rounded-lg border border-[#FF9500]/25 bg-[#FF9500]/10 text-[11px] text-amber-100/90 px-3 py-2 inline-flex items-center gap-2"
          >
            <Compass className="w-3.5 h-3.5 text-[#FF9500]" />
            Tip: tap <b className="text-white">Open in Safari</b> for the native long-press → Save Image gesture.
          </div>
        )}
        <div className="rounded-2xl overflow-hidden bg-black/60 border border-white/5 min-h-[60vh] flex items-center justify-center">
          {loading && !error && (
            <div className="p-8 text-white/50 inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {error ? (
            <div className="p-8 text-center space-y-3">
              <ImageOff className="w-8 h-8 text-white/40 mx-auto" />
              <div className="text-sm text-white/70">Couldn&apos;t load this photo.</div>
              <a
                data-testid="photo-viewer-raw"
                href={imageUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-[#FF9500] hover:text-[#FFB86C]"
              >
                <ExternalLink className="w-4 h-4" /> Try the direct link
              </a>
            </div>
          ) : (
            <img
              data-testid="photo-viewer-img"
              src={imageUrl}
              alt={supplier || "Receipt"}
              onLoad={() => setLoading(false)}
              onError={() => { setLoading(false); setError(true); }}
              className={`max-h-[80vh] w-auto max-w-full object-contain ${loading ? "opacity-0" : "opacity-100"} transition-opacity`}
            />
          )}
        </div>
      </main>

      {/* Bottom action bar — sticky so it's always reachable on tall images. */}
      <div className="sticky bottom-0 z-20 bg-[#1F1F1F]/95 backdrop-blur-md border-t border-white/5">
        <div className="max-w-3xl mx-auto px-4 py-3 grid grid-cols-2 gap-2 pb-safe">
          <button
            data-testid="photo-viewer-share"
            onClick={onShare}
            disabled={sharing}
            className="h-12 rounded-xl bi-accent-btn inline-flex items-center justify-center gap-2 text-sm font-heading font-bold uppercase tracking-wider disabled:opacity-60 active:scale-[0.98] transition"
          >
            {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            {canShare ? "Share photo" : "Share"}
          </button>
          <a
            data-testid="photo-viewer-open-new"
            href={imageUrl}
            target="_blank"
            rel="noreferrer"
            className="h-12 rounded-xl bg-white/5 hover:bg-white/10 inline-flex items-center justify-center gap-2 text-sm font-heading font-bold uppercase tracking-wider active:scale-[0.98] transition"
          >
            <ExternalLink className="w-4 h-4" /> Open in Safari
          </a>
        </div>
      </div>
    </div>
  );
}
